import { toast } from "sonner";

import ApiService from "../services/api-service";

// Web push for the website: desktop and Android browsers, and iPhones once the
// site is added to the Home Screen (iOS 16.4+; Safari tabs can't get push).
// Firebase Cloud Messaging issues the token; public/sw-push.js shows the
// notifications. The Firebase SDK is only loaded when it's needed.

export type WebPushStatus = "unsupported" | "unconfigured" | "needs-install" | "prompt" | "granted" | "denied";

// Same events as lib/push.ts (kept here as strings to avoid an import cycle).
const PUSH_OPEN_PATH_EVENT = "inspirelens:pushOpenPath";
const NOTIFICATIONS_CHANGED_EVENT = "inspirelens:notificationsChanged";
const DEVICE_ID_KEY = "inspirelens-device-id";
const SERVICE_WORKER_URL = "/sw-push.js";

const env = import.meta.env;
// Firebase console → Project settings → General → Your apps → Web app config.
const firebaseConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY as string | undefined,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined,
  projectId: env.VITE_FIREBASE_PROJECT_ID as string | undefined,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID as string | undefined,
  appId: env.VITE_FIREBASE_APP_ID as string | undefined,
};
// Firebase console → Project settings → Cloud Messaging → Web Push certificates.
// Optional: without it the Firebase SDK uses its default key.
const vapidKey = (env.VITE_FIREBASE_VAPID_KEY as string | undefined) || undefined;

const isConfigured = () =>
  Boolean(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.messagingSenderId && firebaseConfig.appId);

const isIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// Opened from the Home Screen (or installed as an app) rather than a browser tab.
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

const isSupported = () =>
  typeof window !== "undefined" &&
  window.isSecureContext &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

let token: string | null = null;
let initialized = false;
let messagingPromise: Promise<import("firebase/messaging").Messaging> | null = null;

function getDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

function getMessaging() {
  messagingPromise ??= (async () => {
    const [{ initializeApp, getApps }, messaging] = await Promise.all([import("firebase/app"), import("firebase/messaging")]);
    const app = getApps()[0] ?? initializeApp(firebaseConfig);
    const instance = messaging.getMessaging(app);
    // The site is open: the service worker doesn't show the notification, so
    // show a banner and refresh the bell.
    messaging.onMessage(instance, (payload) => {
      window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT));
      const path = payload.data?.deepLink || payload.data?.url;
      toast(payload.notification?.title || "The Deepam", {
        description: payload.notification?.body,
        duration: 5000,
        action:
          typeof path === "string" && path.startsWith("/") && !path.startsWith("//")
            ? { label: "View", onClick: () => openPath(path, payload.data?.notificationId) }
            : undefined,
      });
    });
    return instance;
  })();
  return messagingPromise;
}

function openPath(path: string, notificationId?: string | null) {
  const id = Number(notificationId) || undefined;
  window.dispatchEvent(
    new CustomEvent(PUSH_OPEN_PATH_EVENT, { detail: { path, personal: Boolean(id), notificationId: id } })
  );
}

// Gets this browser's FCM token and links it to whoever is signed in.
async function registerToken(): Promise<void> {
  const registration = await navigator.serviceWorker.register(SERVICE_WORKER_URL);
  await navigator.serviceWorker.ready;
  const messaging = await getMessaging();
  const { getToken } = await import("firebase/messaging");
  token = await getToken(messaging, { ...(vapidKey ? { vapidKey } : {}), serviceWorkerRegistration: registration });
  if (!token) return;
  await ApiService.registerPushDevice({ token, platform: "web", deviceId: getDeviceId() || undefined });
}

// What this browser lacks for web push, for the "not available" message
// (e.g. ["PushManager", "Notification"]). Empty when everything is there.
export function getWebPushMissingFeatures(): string[] {
  if (typeof window === "undefined") return [];
  const missing: string[] = [];
  if (!window.isSecureContext) missing.push("secure connection (https)");
  if (!("serviceWorker" in navigator)) missing.push("service workers");
  if (!("PushManager" in window)) missing.push("PushManager");
  if (!("Notification" in window)) missing.push("Notification");
  return missing;
}

export const isIosDevice = () => typeof navigator !== "undefined" && isIos();

export function getWebPushStatus(): WebPushStatus {
  if (typeof window === "undefined") return "unsupported";
  if (!isConfigured()) return "unconfigured";
  // iPhone Safari only allows push for sites added to the Home Screen.
  if (isIos() && !isStandalone()) return "needs-install";
  if (!isSupported()) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  return "prompt";
}

// Call from a button the user tapped (browsers require that for the prompt).
export async function enableWebPush(): Promise<WebPushStatus> {
  const status = getWebPushStatus();
  if (status !== "prompt" && status !== "granted") return status;
  const permission = status === "granted" ? "granted" : await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "denied" : "prompt";
  try {
    await registerToken();
  } catch (error) {
    console.warn("Web push registration failed:", error);
  }
  return "granted";
}

// Page load: wires up taps from the service worker and, if notifications were
// already allowed, refreshes this browser's token. Never shows the prompt.
export async function initWebPush(): Promise<void> {
  if (initialized || !isSupported()) return;
  initialized = true;

  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === "deepam:notification-click") {
      openPath(event.data.path, event.data.notificationId);
    }
  });
  // Sign-in/out relinks this browser to the right account (or none).
  window.addEventListener("inspirelens:userchange", () => {
    if (token) {
      ApiService.registerPushDevice({ token, platform: "web", deviceId: getDeviceId() || undefined }).catch(() => {});
    }
  });

  if (getWebPushStatus() === "granted") {
    registerToken().catch((error) => console.warn("Web push registration failed:", error));
  }
}
